import {useEffect, useMemo} from 'react';
import {useGuest, useGuestGroup, usePreloadGuestData} from '../../hooks';
import {useReservation} from '../../context/reservation';
import {
  getPoliceTypeIfPoliceActive,
  Guest,
  POLICE_CODES,
  COUNTRY_CODES,
  GUEST_FORM_NAMES,
} from '@chekin/core';
import ChekinProSettings from 'ChekinProSettings';

type GetIsPreloadingDataProps = {
  countryCode?: string;
  policeType?: string;
  guest?: Guest;
};
const getPreloadingData = ({
  countryCode = '',
  policeType = '',
  guest,
}: GetIsPreloadingDataProps) => {
  if (!countryCode || !guest) {
    return;
  }

  switch (countryCode) {
    case COUNTRY_CODES.france:
      return {
        [GUEST_FORM_NAMES.residence_country]: guest.residence_country,
        [GUEST_FORM_NAMES.residence_city]: guest.residence_city,
        [GUEST_FORM_NAMES.residence_address]: guest.residence_address,
      };
    case COUNTRY_CODES.spain:
      if ([POLICE_CODES.mossos, POLICE_CODES.ertzaintza].includes(policeType)) {
        return {
          [GUEST_FORM_NAMES.phone]: guest.phone,
          [GUEST_FORM_NAMES.email]: guest.email,
        };
      }
      break;
    case COUNTRY_CODES.germany:
      if (policeType === POLICE_CODES.germanyAVS) {
        return {
          [GUEST_FORM_NAMES.email]: guest.email,
        };
      }
  }
};

type UsePreloadLeaderDataProps = {
  countryCode?: string;
};
export function usePreloadLeaderData({countryCode}: UsePreloadLeaderDataProps) {
  const {guestGroup} = useGuestGroup();
  const {housing} = useReservation();
  const leaderGuest = guestGroup?.leader_id;
  const {guest, isGuestLoaded} = useGuest({id: leaderGuest});
  const preloadingData = useMemo(
    () =>
      getPreloadingData({
        guest,
        countryCode,
        policeType: getPoliceTypeIfPoliceActive(housing),
      }),
    [countryCode, guest, housing],
  );

  const {preloadPersistedFormData} = usePreloadGuestData({
    guestData: preloadingData,
  });

  const shouldPreload =
    Boolean(preloadingData) && ChekinProSettings.appVariant !== 'airbnb';
  useEffect(() => {
    if (leaderGuest && isGuestLoaded && shouldPreload) {
      preloadPersistedFormData(leaderGuest, true);
    }
  }, [leaderGuest, preloadPersistedFormData, isGuestLoaded, shouldPreload]);
}
